import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Icons } from '../../../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { FilesService } from '../../../../shared/services/files.service';
import { ESectionTypes } from '../../../../shared/models/eop_types.enum';
import { FormControl } from '@angular/forms';
import { fileUploadValidator } from '../../../../shared/helpers/fileUploadValidation';
import { NoEmojiDirective } from '../../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'custom-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    NoEmojiDirective,
    SpinnerComponent
  ],
  templateUrl: './custom-file-upload.component.html',
  styleUrl: './custom-file-upload.component.css'
})

export class CustomFileUploadComponent {
  @Input() pageId: string | null = null;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  icons = Icons;

  selectedFiles: { file: File; progress: number; }[] = [];
  fileError: string | null = null;

  loading: boolean = false;
  isDragging: boolean = false;
  
  constructor(
    private filesService: FilesService
  ) {}

  filesControl = new FormControl<File[]>([], fileUploadValidator());

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const files = Array.from(input.files);
    this.filesControl.setValue(files);
    this.filesControl.markAsTouched();
    this.filesControl.updateValueAndValidity();

    this.selectedFiles = []; // Clear previous files
    files.forEach(file => {
      const newFile = { file, progress: 0 };
      this.selectedFiles.push(newFile);
      if (this.filesControl.valid) { // Only simulate progress if files are valid
        this.simulateFakeProgress(newFile);
      }
    });
  
    input.value = ''; // reset so user can reselect same files
  }
  
  simulateFakeProgress(fileObj: any) {
    const interval = setInterval(() => {
      if (fileObj.progress < 100) {
        fileObj.progress += 5;
      } else {
        clearInterval(interval);
      }
    }, 30);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault(); // necessary to allow drop
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  handleFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    if (!event.dataTransfer?.files) return;

    const files = Array.from(event.dataTransfer.files);
    this.filesControl.setValue(files);
    this.filesControl.markAsTouched();
    this.filesControl.updateValueAndValidity();

    this.selectedFiles = []; // Clear previous files
  
    Array.from(files).forEach(file => {
      const newFile = { file, progress: 0 };
      this.selectedFiles.push(newFile);
      if (this.filesControl.valid) { // Only simulate progress if files are valid
        this.simulateFakeProgress(newFile);
      }
    });
  }

  handleSaveFiles(): void {
    if (this.filesControl.valid && this.selectedFiles.length > 0 && this.pageId) {
      this.loading = true;
      const formData = new FormData();
      this.selectedFiles.forEach(fileObj => {
        formData.append('files', fileObj.file, fileObj.file.name);
      });
      this.filesService.uploadFile(String(ESectionTypes["Custom EOP Page"]), Number(this.pageId), formData).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.onSave.emit({'type': 'success', 'message': 'This occurred after uploading files.'});
          } else {
            this.onSave.emit({'type': 'unsuccess', 'message': response.message});
          }
        },
        error: (error) => {
          this.loading = false;
          this.onSave.emit({'type': 'unsuccess', 'message': error.message});
        }
      });
      return;
    }
  }

  handleCloseFileUpload(): void {
    this.onClose.emit();
  }

  getTruncatedFileName(name: string, maxLength: number): string {
    if (name.length <= maxLength) return name;
  
    const dotIndex = name.lastIndexOf('.');
    const base = name.substring(0, dotIndex);
    const ext = name.substring(dotIndex);
  
    const prefix = base.substring(0, Math.floor((maxLength - ext.length) / 2));
    const suffix = base.substring(base.length - Math.floor((maxLength - ext.length) / 2));
  
    return `${prefix}…${suffix}${ext}`;
  }
}
